import { firebase_app } from "../Config/Config";

const ADMIN_MUTATION_PATH = "/admin-data-mutation";
const ADMIN_OPERATIONS = new Set([
  "contract.create",
  "contract.update",
  "contract.delete",
  "user.create",
  "user.update",
  "user.delete",
]);
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const OPERATION_STORAGE_PREFIX = 'maxpark.adminMutation.v1.';
const OPERATION_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

const stableValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(stableValue);
  }

  if (value && typeof value === "object") {
    return Object.keys(value).sort().reduce((result, key) => {
      if (value[key] !== undefined) {
        result[key] = stableValue(value[key]);
      }
      return result;
    }, {});
  }

  return value;
};

const submissionFingerprint = ({ operation, subjectId, fields }) =>
  JSON.stringify(stableValue({ operation, subjectId, fields }));

const fallbackFingerprintHash = (value) => {
  const hashWithSeed = (seed) => {
    let hash = seed ^ value.length;
    for (let index = 0; index < value.length; index += 1) {
      hash = Math.imul(hash ^ value.charCodeAt(index), 2654435761);
      hash = (hash << 13) | (hash >>> 19);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  };

  return [0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f]
    .map(hashWithSeed)
    .join('');
};

const hashSubmissionFingerprint = async (value) => {
  if (globalThis.crypto?.subtle && globalThis.TextEncoder) {
    const bytes = new globalThis.TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return fallbackFingerprintHash(value);
};

const operationStorage = () => {
  try {
    return globalThis.sessionStorage || null;
  } catch (error) {
    return null;
  }
};

const readStoredOperationId = (storageKey) => {
  const storage = operationStorage();
  if (!storage) return '';

  try {
    const stored = JSON.parse(storage.getItem(storageKey) || 'null');
    const isCurrent = stored &&
      OPERATION_ID_PATTERN.test(String(stored.operationId || '')) &&
      Number.isFinite(stored.createdAt) &&
      Date.now() - stored.createdAt <= OPERATION_STORAGE_TTL_MS;

    if (isCurrent) return stored.operationId;
    storage.removeItem(storageKey);
  } catch (error) {
    try {
      storage.removeItem(storageKey);
    } catch (storageError) {
      return '';
    }
  }

  return '';
};

const storeOperationId = (storageKey, operationId) => {
  try {
    operationStorage()?.setItem(storageKey, JSON.stringify({
      operationId,
      createdAt: Date.now(),
    }));
  } catch (error) {
    // The in-memory tracker still preserves retries when storage is unavailable.
  }
};

const removeStoredOperationId = (storageKey) => {
  try {
    operationStorage()?.removeItem(storageKey);
  } catch (error) {
    // A confirmed server success must not be hidden by a storage cleanup failure.
  }
};

const randomOperationId = () => {
  const randomPart = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID().replace(/-/g, "")
    : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;

  return `admin_${Date.now().toString(36)}_${randomPart}`.slice(0, 128);
};

export class AdminMutationError extends Error {
  constructor(message, { status = 0, code = "ADMIN_MUTATION_FAILED" } = {}) {
    super(message);
    this.name = "AdminMutationError";
    this.status = status;
    this.code = code;
  }
}

export const createAdminOperationTracker = () => {
  let operationId = "";
  let fingerprintHash = "";
  let storageKey = "";
  let queue = Promise.resolve();

  return {
    forSubmission(input) {
      const resolveOperationId = async () => {
        const nextFingerprintHash = await hashSubmissionFingerprint(submissionFingerprint(input));
        const nextStorageKey = `${OPERATION_STORAGE_PREFIX}${nextFingerprintHash}`;

        if (operationId && fingerprintHash === nextFingerprintHash) {
          return operationId;
        }

        operationId = readStoredOperationId(nextStorageKey) || randomOperationId();
        fingerprintHash = nextFingerprintHash;
        storageKey = nextStorageKey;
        storeOperationId(storageKey, operationId);
        return operationId;
      };
      const queued = queue.then(resolveOperationId, resolveOperationId);
      queue = queued.then(() => undefined, () => undefined);
      return queued;
    },
    markSucceeded() {
      if (storageKey) {
        removeStoredOperationId(storageKey);
      }
      operationId = "";
      fingerprintHash = "";
      storageKey = "";
    },
  };
};

const readResponseBody = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

export const performAdminDataMutation = async ({
  operation,
  operationId,
  subjectId = "",
  fields = {},
}) => {
  if (!ADMIN_OPERATIONS.has(operation)) {
    throw new AdminMutationError("Operation administrative invalide.");
  }
  if (!OPERATION_ID_PATTERN.test(String(operationId || ""))) {
    throw new AdminMutationError("Identifiant d'operation invalide.");
  }
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) {
    throw new AdminMutationError("Champs administratifs invalides.");
  }

  const currentUser = firebase_app.auth().currentUser;
  if (!currentUser || typeof currentUser.getIdToken !== "function") {
    throw new AdminMutationError("Une session administrateur est requise.", {
      status: 401,
      code: "ADMIN_AUTH_REQUIRED",
    });
  }

  const idToken = await currentUser.getIdToken(true);
  if (!idToken) {
    throw new AdminMutationError("Une session administrateur est requise.", {
      status: 401,
      code: "ADMIN_AUTH_REQUIRED",
    });
  }

  let response;
  try {
    response = await fetch(ADMIN_MUTATION_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operation,
        operationId,
        subjectId: String(subjectId || ""),
        fields,
      }),
    });
  } catch (error) {
    throw new AdminMutationError("Le service administratif est temporairement indisponible.", {
      code: "ADMIN_NETWORK_FAILURE",
    });
  }

  const responseBody = await readResponseBody(response);
  if (!response.ok || responseBody?.status !== "success") {
    const code = String(responseBody?.code || "ADMIN_MUTATION_FAILED");
    const messages = {
      USER_EMAIL_CONFLICT: "Ce courriel est déjà utilisé. Ouvre le profil existant dans la liste des utilisateurs pour le modifier.",
      ADMIN_ACCESS_PROJECTION_TIMEOUT: "La synchronisation des droits n'est pas terminée. Réessaye avec les mêmes informations dans ce formulaire.",
    };
    throw new AdminMutationError(messages[code] || "L'operation administrative n'a pas ete confirmee.", {
      status: response.status,
      code,
    });
  }

  return responseBody;
};
